import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { escrowApi } from "@/api/escrow";
import { extractErrorMessage } from "@/api/client";
import { INSPECTION_PERIOD_OPTIONS } from "@/types/escrow";
import type { DealDetailDto } from "@/types/deals";

interface Props {
  open: boolean;
  deal: DealDetailDto | null;
  onClose: () => void;
}

const DEFAULT_INSPECTION = String(INSPECTION_PERIOD_OPTIONS[1].seconds); // 3 days

/**
 * Escrow.com Integration — launches a draft escrow transaction from the deal
 * header. The buyer is pulled from the deal's borrower server-side; Phoenix
 * Capital is the fixed counterparty. The draft must be approved by an ADMIN
 * before it is sent to Escrow.com.
 */
export function CreateEscrowModal({ open, deal, onClose }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [inspection, setInspection] = useState(DEFAULT_INSPECTION);
  // Escrow-scoped "Funds from" override. `overrideActive` toggles the editable
  // input; `overrideEmail` holds the typed value (prefilled with canonical).
  const [overrideActive, setOverrideActive] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");

  // Pre-draft preview of the resolved "Funds from" email (the deal borrower's
  // counterparty email sent to Escrow.com), so staff catch a wrong/missing
  // email before submitting.
  const buyerEmailQuery = useQuery({
    queryKey: ["escrow", "buyer-email", deal?.id],
    queryFn: () => escrowApi.buyerEmailPreview(deal!.id),
    enabled: open && !!deal,
  });
  const canonicalEmail = buyerEmailQuery.data?.buyerEmail ?? null;

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
      setInspection(DEFAULT_INSPECTION);
      setOverrideActive(false);
      setOverrideEmail("");
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!deal) throw new Error("No deal selected.");
      // Only send the override when it's active AND actually differs from the
      // canonical email — otherwise the backend records canonical for both.
      const trimmedOverride = overrideEmail.trim();
      const sendOverride =
        overrideActive &&
        trimmedOverride !== "" &&
        trimmedOverride.toLowerCase() !== (canonicalEmail ?? "").trim().toLowerCase();
      return escrowApi.draft({
        dealId: deal.id,
        amount: Number(amount),
        itemDescription: description.trim(),
        inspectionPeriodSeconds: Number(inspection),
        ...(sendOverride ? { buyerEmailOverride: trimmedOverride } : {}),
      });
    },
    onSuccess: (created) => {
      toast.success("Escrow draft created.");
      queryClient.invalidateQueries({ queryKey: ["escrow"] });
      onClose();
      navigate(`/escrow/${created.id}`);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (!deal) return null;

  const amountNum = Number(amount);
  const valid =
    amount.trim() !== "" &&
    Number.isFinite(amountNum) &&
    amountNum > 0 &&
    description.trim() !== "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Create Escrow Transaction"
      description={`${deal.dealNumber} — buyer pulled from the deal; Phoenix Capital is the counterparty.`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!valid} loading={mutation.isPending}>
            Create draft
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <p className="text-small text-gray-mid">
          The draft is created in <strong>Pending approval</strong>. An administrator
          must approve it before it is sent to Escrow.com. Funds are paid by the
          deal borrower, who covers all Escrow.com fees; 100% of the amount is
          disbursed to Phoenix Capital.
        </p>

        <FundsFromField
          loading={buyerEmailQuery.isLoading}
          email={canonicalEmail}
          overrideActive={overrideActive}
          overrideEmail={overrideEmail}
          onOverrideEmailChange={setOverrideEmail}
          onActivateOverride={() => {
            setOverrideEmail(canonicalEmail ?? "");
            setOverrideActive(true);
          }}
          onCancelOverride={() => {
            setOverrideActive(false);
            setOverrideEmail("");
          }}
        />

        <FormField label="Amount (USD)" htmlFor="escrow-amount" required>
          <Input
            id="escrow-amount"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </FormField>

        <FormField label="Item description" htmlFor="escrow-desc" required>
          <Textarea
            id="escrow-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Closing costs and underwriting fees"
          />
        </FormField>

        <FormField label="Inspection period" htmlFor="escrow-inspection" required>
          <Select
            id="escrow-inspection"
            value={inspection}
            onChange={(e) => setInspection(e.target.value)}
          >
            {INSPECTION_PERIOD_OPTIONS.map((o) => (
              <option key={o.seconds} value={o.seconds}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
    </Modal>
  );
}

interface FundsFromFieldProps {
  loading: boolean;
  email: string | null;
  overrideActive: boolean;
  overrideEmail: string;
  onOverrideEmailChange: (value: string) => void;
  onActivateOverride: () => void;
  onCancelOverride: () => void;
}

/**
 * Shows the resolved "Funds from" email (the deal borrower's counterparty
 * email) before submit so a wrong/missing email is caught in Phoenix's UI, and
 * lets staff override it for THIS escrow transaction only (e.g. a backup
 * contact or spouse) without touching the deal record. Phoenix Capital is the
 * Loan Administrator — we use "Funds from", never "Buyer".
 */
function FundsFromField({
  loading,
  email,
  overrideActive,
  overrideEmail,
  onOverrideEmailChange,
  onActivateOverride,
  onCancelOverride,
}: FundsFromFieldProps) {
  return (
    <div className="rounded-md border border-border-light bg-white-warm p-space-3">
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        Funds from
      </div>

      {loading ? (
        <p className="text-small text-gray-mid italic m-0 mt-space-1">Resolving…</p>
      ) : overrideActive ? (
        <div className="mt-space-1 flex flex-col gap-space-1">
          <Input
            aria-label="Override Funds from email"
            type="email"
            value={overrideEmail}
            onChange={(e) => onOverrideEmailChange(e.target.value)}
            placeholder="name@example.com"
          />
          <p className="text-caption text-gray-mid m-0">
            Override applies only to this escrow transaction. The deal record will
            not be changed.
          </p>
          <Button
            variant="text"
            size="sm"
            className="self-start px-0"
            onClick={onCancelOverride}
          >
            Cancel override
          </Button>
        </div>
      ) : email ? (
        <div className="mt-space-1 flex flex-col gap-space-1">
          <p className="text-small text-onyx m-0 break-all">{email}</p>
          <Button
            variant="text"
            size="sm"
            className="self-start px-0"
            onClick={onActivateOverride}
          >
            Use a different email
          </Button>
        </div>
      ) : (
        <p className="text-small text-status-error m-0 mt-space-1">
          No borrower email is set on this deal. Set the borrower&apos;s email on the
          counterparty record before approving — the transaction can&apos;t be sent
          without it.
        </p>
      )}
    </div>
  );
}
